import { call, put } from 'redux-saga/effects';
import { bulkUpdateExcelTransactions } from '../api';
import {
  bulkUpdateExcelTransactionsFailure,
  bulkUpdateExcelTransactionsSuccess,
  fetchAllExcelTransactionsRequest,
} from '../actions';
import { ApiError, ApiResponse } from 'store/api/types';
import { checkResponse } from 'store/utils';
import { BulkUpdateExcelTransactionsRequestAction } from '../types';
import { MessageData } from 'store/app/types';
import { turnMessageOn } from 'store/app/actions';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import { buildTransactionsRefreshFilters } from 'utils/entityTransactionsRequestFilters';
import { MessageStates } from 'containers/MessageBox';
import i18n from 'i18next';

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* bulkUpdateExcelTransactionsSaga(
  action: BulkUpdateExcelTransactionsRequestAction
) {
  try {
    const { organisationId, ids, updates, page, pageSize, filters, tabFileImportIds } =
      action.payload;

    if (!organisationId || !ids?.length || !updates || !Object.keys(updates).length) {
      yield put(bulkUpdateExcelTransactionsFailure());
      return;
    }

    const response: ApiResponse = yield call(bulkUpdateExcelTransactions, organisationId, {
      ids,
      updates,
    });

    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    const defaultDescription =
      ids.length === 1
        ? 'The transaction has been updated successfully.'
        : 'The selected transactions have been updated successfully.';
    const message: MessageData = {
      title: i18n.t('common.success'),
      description:
        response?.responseMessage?.message ?? defaultDescription,
      type: MessageStates.SUCCESS,
    };
    yield put(turnMessageOn(message));
    yield put(bulkUpdateExcelTransactionsSuccess());

    const effectivePage = page ?? 1;
    const effectivePageSize = pageSize ?? 10;
    // Do not re-apply file-panel excelMetadataId from mass-update apply time — that
    // captured a subset (often a single selected upload) and causes a second fetch
    // that overwrites the full tab view after save.
    const refreshFilters = buildTransactionsRefreshFilters({
      tableFilters: filters,
      tabFileImportIds,
    });
    yield put(
      fetchAllExcelTransactionsRequest(
        organisationId,
        effectivePage,
        effectivePageSize,
        refreshFilters
      )
    );
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(bulkUpdateExcelTransactionsFailure());
  }
}
